import { Link } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Accordion from "@/components/feature/Accordion";
import Reveal from "@/components/base/Reveal";
import { SectionHeading } from "@/components/base/Shared";
import { milestones, principles, siteMeta, testimonials } from "@/mocks/site";
import { aboutFaqs } from "@/mocks/faq";

const ABOUT_JSONLD = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于瓜链 · 网红黑曝传播追踪",
    description:
      "瓜链是聚焦网红黑曝发酵、上热搜与病毒传播路径的结构化记录平台，介绍品牌定位、内容原则与联系方式。",
    inLanguage: "zh-CN",
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "瓜链",
    alternateName: "GuaLink",
    email: "info@gualian.example.com",
    description: "专注网红黑曝话题发酵与病毒传播路径的结构化记录平台。",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://gualian.example.com/" },
      { "@type": "ListItem", position: 2, name: "关于我们", item: "https://gualian.example.com/about" },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: aboutFaqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: { "@type": "Answer", text: faq.a },
    })),
  },
];

export default function About() {
  return (
    <>
      <Seo
        title="关于瓜链 - 网红黑曝发酵与传播追踪平台介绍"
        description="瓜链专注网红黑曝发酵与病毒传播追踪，用热度值、话题量、转发量与扩散渠道记录事件的传播链路。了解我们的品牌定位、内容原则与联系方式。"
        keywords="网红黑曝,网红黑曝发酵,传播追踪平台,关于瓜链,网红黑曝话题量"
        canonicalPath="/about"
        jsonLd={ABOUT_JSONLD}
      />
      <div className="min-h-screen bg-background-50">
        <SiteHeader />

        <section className="bg-background-100 border-b border-background-200 pt-28 md:pt-32 pb-12 md:pb-16">
          <div className="w-full px-4 md:px-8">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <span className="inline-flex items-center gap-2 text-xs font-label tracking-[0.2em] text-primary-600 uppercase">
                  <span className="w-6 h-px bg-primary-400" />
                  ABOUT GUALINK
                </span>
                <h1 className="mt-4 font-heading text-3xl md:text-5xl font-black text-foreground-950 tracking-tight leading-tight">
                  关于瓜链
                </h1>
                <p className="mt-5 text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
                  瓜链是一个专注<strong className="text-foreground-900 font-normal">网红黑曝发酵与病毒传播追踪</strong>的内容平台。
                  我们不生产爆料，也不对事件下结论，只做一件事：把一条信息从出现到扩散的每一步记录下来，
                  让热度这件事变得可读、可对比、可复盘。
                </p>
                <div className="mt-7 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-foreground-600">
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                      <i className="ri-mail-line" />
                    </span>
                    {siteMeta.email}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center text-secondary-600">
                      <i className="ri-time-line" />
                    </span>
                    {siteMeta.workHours}
                  </span>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="relative rounded-2xl overflow-hidden border border-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=abstract%20editorial%20scene%20of%20interconnected%20amber%20glowing%20nodes%20and%20curved%20lines%20on%20deep%20charcoal%20background%2C%20data%20art%20representing%20information%20spread%2C%20warm%20minimal%20composition%2C%20soft%20grain%2C%20no%20text&width=1200&height=900&seq=gualian-about-story&orientation=landscape"
                    alt="瓜链传播追踪数据可视化示意"
                    title="瓜链 · 网红黑曝传播路径记录"
                    className="w-full h-[280px] md:h-[340px] object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 to-transparent" />
                  <p className="absolute bottom-5 left-5 right-5 text-sm text-background-50 leading-relaxed">
                    每一次扩散都有节点，每一个节点都可以被记录。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-16 md:py-24">
          <div className="w-full px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
              <div className="lg:col-span-5">
                <SectionHeading
                  eyebrow="POSITIONING"
                  title="我们想解决什么问题"
                  desc="事件热度往往只有「火」和「不火」两个印象，中间的传播过程却被忽略掉了。"
                />
              </div>
              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
                {[
                  {
                    icon: "ri-graduation-cap-line",
                    title: "把过程变成可读的结构",
                    desc: "首爆时间、话题量变化、转发增速、扩散渠道，按统一口径记录，形成可对比的样本。",
                  },
                  {
                    icon: "ri-compass-3-line",
                    title: "给内容从业者一个参照",
                    desc: "复盘舆情时最缺的往往是顺序与节点，我们提供的正是这部分可以核对的时间轴。",
                  },
                  {
                    icon: "ri-user-voice-line",
                    title: "给普通读者一个入口",
                    desc: "不必追每一条动态，也能用几分钟看懂一个话题为什么能在一夜之间铺满首页。",
                  },
                  {
                    icon: "ri-shield-check-line",
                    title: "保持克制的记录立场",
                    desc: "不下结论、不评价当事人，只呈现信息如何流动，把判断权留给读者自己。",
                  },
                ].map((item, index) => (
                  <Reveal key={item.title} delay={index * 80}>
                    <div className="h-full rounded-2xl bg-background-100 border border-background-200 p-6">
                      <span className="w-11 h-11 rounded-xl bg-background-50 border border-background-200 text-primary-600 flex items-center justify-center">
                        <i className={`${item.icon} text-xl`} />
                      </span>
                      <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="principles" className="bg-background-100 border-y border-background-200 py-16 md:py-24">
          <div className="w-full px-4 md:px-8">
            <SectionHeading
              eyebrow="CONTENT PRINCIPLES"
              title="内容原则：记录传播，不评判是非"
              desc="这四条原则决定了我们收录什么、怎么标注，以及为什么有些说法不会出现在时间线里。"
              align="center"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {principles.map((item, index) => (
                <Reveal key={item.title} delay={index * 80}>
                  <div className="h-full rounded-2xl bg-background-50 border border-background-200 p-6">
                    <span className="w-12 h-12 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center">
                      <i className={`${item.icon} text-xl`} />
                    </span>
                    <h3 className="mt-5 font-heading text-base font-bold text-foreground-950">
                      {item.title}
                    </h3>
                    <p className="mt-2.5 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-16 md:py-24">
          <div className="w-full px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
              <div className="lg:col-span-4">
                <SectionHeading
                  eyebrow="MILESTONES"
                  title="瓜链是怎么长出来的"
                  desc="从一份内部表格，到一套公开的观察口径，过程比结果更直白。"
                />
              </div>
              <div className="lg:col-span-8">
                <ol className="relative">
                  <span className="absolute left-[27px] top-4 bottom-4 w-px bg-background-300" aria-hidden="true" />
                  {milestones.map((item, index) => (
                    <Reveal key={item.year} delay={index * 90}>
                      <li className="relative flex gap-6 pb-8 last:pb-0">
                        <span className="relative z-10 w-14 h-14 shrink-0 rounded-2xl bg-primary-500 text-background-50 flex items-center justify-center font-heading text-sm font-bold">
                          {item.year}
                        </span>
                        <div className="pt-1.5">
                          <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950">
                            {item.title}
                          </h3>
                          <p className="mt-2 text-sm text-foreground-600 leading-relaxed max-w-2xl">
                            {item.desc}
                          </p>
                        </div>
                      </li>
                    </Reveal>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 border-y border-background-200 py-16 md:py-24">
          <div className="w-full px-4 md:px-8">
            <SectionHeading
              eyebrow="VOICES"
              title="读者怎么说"
              desc="来自品牌公关、内容编辑与普通读者的反馈，也是我们继续打磨观察口径的理由。"
              align="center"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((item, index) => (
                <Reveal key={item.author} delay={index * 90}>
                  <figure className="h-full flex flex-col rounded-2xl bg-background-50 border border-background-200 p-6">
                    <span className="w-9 h-9 rounded-lg bg-accent-50 text-accent-700 flex items-center justify-center">
                      <i className="ri-double-quotes-l text-lg" />
                    </span>
                    <blockquote className="mt-4 flex-1 text-sm text-foreground-700 leading-relaxed">
                      {item.quote}
                    </blockquote>
                    <figcaption className="mt-5 pt-4 border-t border-background-200">
                      <span className="block font-heading text-sm font-bold text-foreground-950">
                        {item.author}
                      </span>
                      <span className="mt-0.5 block text-xs text-foreground-500">{item.role}</span>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-16 md:py-24">
          <div className="w-full px-4 md:px-8">
            <div className="max-w-3xl mx-auto">
              <SectionHeading
                eyebrow="FAQ"
                title="关于瓜链的常见问题"
                desc="如果你还想了解我们的内容口径与整理方式，这里或许有答案。"
                align="center"
              />
              <Accordion items={aboutFaqs} />
            </div>
          </div>
        </section>

        <section id="contact" className="bg-background-100 border-t border-background-200 py-14 md:py-20">
          <div className="w-full px-4 md:px-8">
            <div className="rounded-2xl bg-secondary-900 text-background-50 p-7 md:p-10">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold">联系我们</h2>
                  <p className="mt-3 text-sm md:text-base text-secondary-200 leading-relaxed max-w-xl">
                    对页面上的数据口径有疑问，或者想补充某条事件的传播节点，欢迎通过邮件联系我们。
                    我们会在值守时段内回复，并核对后更新页面记录。
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                  <a
                    href={`mailto:${siteMeta.email}`}
                    className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label whitespace-nowrap transition-colors"
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-mail-send-line" />
                    </span>
                    {siteMeta.email}
                  </a>
                  <Link
                    to="/#subscribe"
                    className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md border border-secondary-700 hover:bg-secondary-800 text-background-50 text-sm font-label whitespace-nowrap transition-colors"
                  >
                    订阅追踪简报
                  </Link>
                </div>
              </div>
              <div className="mt-8 pt-6 border-t border-secondary-700 grid grid-cols-1 sm:grid-cols-3 gap-5 text-sm">
                <div>
                  <p className="text-secondary-300 text-xs">编辑值守时间</p>
                  <p className="mt-1.5 font-label">{siteMeta.workHours}</p>
                </div>
                <div>
                  <p className="text-secondary-300 text-xs">内容类型</p>
                  <p className="mt-1.5 font-label">传播链路记录与热度数据整理</p>
                </div>
                <div>
                  <p className="text-secondary-300 text-xs">数据更新</p>
                  <p className="mt-1.5 font-label">发酵期按小时观测，长尾事件按日回访</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <SiteFooter />
        <BackToTop />
      </div>
    </>
  );
}